import { get, set } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlineInsertComment } from "react-icons/md";
import Alert from "shared/components/Alert";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import { NAME_SPACES } from "shared/locales/constants";
import { SUBSCRIPTION_FORM_KEYS } from "utils/constants";

const Information = ({
  bindField,
  initialValues,
  setPercent,
  isFieldRequired,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const INFORMATION = t(
    "SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.INFORMATION",
    {
      returnObjects: true,
    },
  );
  const [isPPE, setIsPPE] = useState(
    get(initialValues, "project.fields.isPPE"),
  );

  const buildSwitchField = (path, label, onChangeExtra) => {
    const field = bindField(path, {
      label,
      onChange: (value) => {
        if (typeof onChangeExtra === "function") {
          onChangeExtra(value);
        }
        setPercent(initialValues);
      },
    });
    return (
      <FormField
        key={path}
        path={field.path}
        label={field.label}
        required={field.required}
        disabled={field.disabled}
      >
        {(inputField) => (
          <Input
            type="switch"
            label={label}
            checked={get(initialValues, path) === true}
            {...inputField}
            onChange={field.onChange}
            onBlur={field.onBlur}
          />
        )}
      </FormField>
    );
  };

  const ppeFunctionPath = "project.fields.ppeFunction";
  const ppeFunctionField = bindField(ppeFunctionPath, {
    label: "Quelles sont / étaient les fonctions du prospect ?",
    onChange: () => setPercent(initialValues),
    onBlur: () => setPercent(initialValues),
  });

  const justificationsPath = "project.fields.info.justifications";
  const justificationsField = bindField(justificationsPath, {
    label: INFORMATION.JUSTIFICATIONS?.LABEL || "Justifications",
    required: isFieldRequired(justificationsPath),
    onChange: () => setPercent(initialValues),
    onBlur: () => setPercent(initialValues),
  });

  const commentPath = "project.fields.info.comment";
  const commentField = bindField(commentPath, {
    label: INFORMATION.COMMENT || "Commentaires éventuels",
    required: isFieldRequired(commentPath),
    onChange: () => setPercent(initialValues),
    onBlur: () => setPercent(initialValues),
  });

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.INFORMATION}-section`}
    >
      <Form type="vertical" title={INFORMATION.TITLE}>
        <div className="grid--1">
          {buildSwitchField(
            "project.fields.isPPE",
            "Personne Politiquement Exposée (PPE*)",
            (value) => {
              setIsPPE(value);
              if (!value) {
                set(initialValues, "project.fields.ppeFunction", "");
                set(initialValues, "project.fields.ppeRelated", false);
              }
            },
          )}
          <Alert
            showIcon
            type="info"
            message={
              "* Une Personne Politiquement Exposée (PPE) est une personne qui exerce ou a cessé d'exercer depuis moins d'un an, une fonction publique importante en France, dans un pays étranger ou au sein d'une organisation internationale (ex. chef d'État, ministre, parlementaire, magistrat, dirigeant d'entreprise publique…), ainsi que certains membres de sa famille (conjoint, enfants, parents) ou les personnes connues pour lui être étroitement associées."
            }
          />
          {isPPE && (
            <FormField
              path={ppeFunctionField.path}
              label={ppeFunctionField.label}
              required={ppeFunctionField.required}
              disabled={ppeFunctionField.disabled}
            >
              {(field) => (
                <Input
                  label={ppeFunctionField.label}
                  placeholder={"Saisir la fonction du prospect"}
                  type="custom"
                  normalizeText={true}
                  defaultValue={ppeFunctionField.defaultValue}
                  {...field}
                  onChange={ppeFunctionField.onChange}
                  onBlur={ppeFunctionField.onBlur}
                />
              )}
            </FormField>
          )}
          {buildSwitchField(
            "project.fields.ppeRelated",
            "Lien familial ou association étroite du prospect avec une PPE* ?",
          )}
          {buildSwitchField(
            "project.fields.acceptProductStatuses",
            "Le prospect déclare avoir reçu et pris connaissance, préalablement à l'adhésion au contrat, du document d'information sur le produit d'assurance complémentaire santé, des statuts, du règlement mutualiste, des dispositions particulières, de la notice d'information contractuelle Prévention et conseil médical en ligne à la garantie d'assistance, et en accepter tous les termes.",
          )}
          {buildSwitchField(
            "project.fields.acceptAgreement",
            "Le prospect atteste sur l'honneur de l'exactitude des renseignements complétés.",
          )}
          {buildSwitchField(
            "project.fields.acceptFees",
            `Le prospect est informé que la communication des documents contractuels ainsi que les informations relatives à l'exécution de son contrat d'assurance (communications, avis d'échéance, etc.) est réalisée exclusivement par voie électronique.
Il atteste que l'adresse e-mail transmise est valide et lui permet de recevoir ces informations.`,
          )}
          <FormField
            path={justificationsField.path}
            label={justificationsField.label}
            required={justificationsField.required}
            disabled={justificationsField.disabled}
          >
            {(field) => (
              <Input
                type="textarea"
                label={justificationsField.label}
                placeholder={
                  INFORMATION.JUSTIFICATIONS?.PLACEHOLDER ||
                  "Saisir les justifications"
                }
                icon={<MdOutlineInsertComment size={16} />}
                defaultValue={justificationsField.defaultValue}
                {...field}
                onChange={justificationsField.onChange}
                onBlur={justificationsField.onBlur}
                autoSize={{ minRows: 2, maxRows: 10 }}
                optional={!justificationsField.required}
              />
            )}
          </FormField>
          <FormField
            path={commentField.path}
            label={commentField.label}
            required={commentField.required}
            disabled={commentField.disabled}
          >
            {(field) => (
              <Input
                type="textarea"
                label={commentField.label}
                placeholder={INFORMATION.COMMENT || "Saisir vos commentaires"}
                icon={<MdOutlineInsertComment size={16} />}
                defaultValue={commentField.defaultValue}
                {...field}
                onChange={commentField.onChange}
                onBlur={commentField.onBlur}
                autoSize={{ minRows: 2, maxRows: 10 }}
                optional={!commentField.required}
              />
            )}
          </FormField>
        </div>
      </Form>
    </div>
  );
};

export default Information;
